import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { MessageSquare, X } from 'lucide-react';

export interface AdminInputDialogProps {
    open: boolean;
    title: string;
    description?: React.ReactNode;
    /** Contenido entre cabecera y el campo (p. ej. plantillas de rechazo) */
    beforeField?: React.ReactNode;
    /** Icono principal; por defecto MessageSquare */
    leadingIcon?: React.ReactNode;
    /** Etiqueta sobre el campo */
    fieldLabel?: string;
    placeholder?: string;
    initialValue?: string;
    /** Modo controlado del texto (requiere onValueChange) */
    value?: string;
    onValueChange?: (v: string) => void;
    multiline?: boolean;
    rows?: number;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmIcon?: React.ReactNode;
    /** Si false, no se puede confirmar con texto vacío */
    allowEmpty?: boolean;
    variant?: 'default' | 'amber';
    confirmDisabled?: boolean;
    onConfirm: (value: string) => void;
    onCancel: () => void;
}

export function AdminInputDialog({
    open,
    title,
    description,
    beforeField,
    leadingIcon,
    fieldLabel = 'Texto',
    placeholder = '',
    initialValue = '',
    value: controlledValue,
    onValueChange,
    multiline = true,
    rows = 4,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    confirmIcon,
    allowEmpty = true,
    variant = 'default',
    confirmDisabled = false,
    onConfirm,
    onCancel,
}: AdminInputDialogProps) {
    const isControlled = controlledValue !== undefined;
    const [internal, setInternal] = useState(initialValue);

    useEffect(() => {
        if (!isControlled && open) setInternal(initialValue);
    }, [open, initialValue, isControlled]);

    if (!open) return null;

    const text = isControlled ? controlledValue! : internal;
    const setText = (v: string) => {
        if (isControlled) onValueChange?.(v);
        else setInternal(v);
    };

    const isAmber = variant === 'amber';
    const canSubmit = allowEmpty || text.trim().length > 0;

    const handleConfirm = () => {
        if (!canSubmit) return;
        onConfirm(text.trim());
    };

    const defaultLeading = (
        <MessageSquare size={18} className={cn(isAmber ? 'text-amber-400' : 'text-primary')} />
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={onCancel}
                aria-hidden
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-input-title"
                className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl animate-in zoom-in-95 fade-in duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start gap-3 p-5 border-b border-border/50">
                    <div
                        className={cn(
                            'p-2 rounded-xl shrink-0',
                            isAmber ? 'bg-amber-500/10' : 'bg-primary/10'
                        )}
                    >
                        {leadingIcon ?? defaultLeading}
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                        <h3 id="admin-input-title" className="text-base font-semibold text-foreground">
                            {title}
                        </h3>
                        {description != null && description !== '' && (
                            <div className="mt-2 text-sm text-muted-foreground">{description}</div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shrink-0"
                        aria-label="Cerrar"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    {beforeField}
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {fieldLabel}
                        </label>
                        {multiline ? (
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={placeholder}
                                rows={rows}
                                className={cn(
                                    'mt-1.5 w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none resize-y min-h-[80px]',
                                    isAmber ? 'focus:ring-1 focus:ring-amber-500/50' : 'focus:ring-1 focus:ring-primary/50'
                                )}
                            />
                        ) : (
                            <input
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={placeholder}
                                className={cn(
                                    'mt-1.5 w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none',
                                    isAmber ? 'focus:ring-1 focus:ring-amber-500/50' : 'focus:ring-1 focus:ring-primary/50'
                                )}
                            />
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 p-5 pt-0">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        disabled={confirmDisabled || !canSubmit}
                        onClick={handleConfirm}
                        className={cn(
                            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none',
                            isAmber
                                ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 border border-amber-500/25'
                                : 'bg-primary text-primary-foreground hover:bg-primary/90'
                        )}
                    >
                        {confirmIcon}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
